import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { firestore } from './firebase';

// One sound's browse metadata, as stored in a sound_packs_sounds document.
// Mirrors the fields the plugin browse list consumes — playback data (layers,
// slices) is NOT here; it lives in the on-disk descriptors shipped in the pack
// zip and is only needed once the pack's audio is downloaded. `tagIds` /
// `instrumentTypeIds` reference the sound_tags / sound_instrument_types
// vocabulary by id.
export type DatabaseSound = {
    uid: string;
    title: string;
    isDemo: boolean;
    key: string;
    bpm: number;
    // The raw authored `loop` flag from the descriptor. On its own it means
    // nothing: a sound is a loop only when it ALSO has a bpm (see
    // docs/architecture/sound-types.md and helpers/soundType.ts). Packs have
    // shipped a stray `true` here on non-loops, so never branch on it alone.
    loop: boolean;
    duration: number; // seconds; audio-derived, baked at delivery prep
    // Browse waveform thumbnail: base64-packed absolute peak bytes (one per
    // column, 0..255), at Sound::browseThumbnailResolution. Empty when unknown.
    thumbnail: string;
    tagIds: string[];
    instrumentTypeIds: string[];
    // Explicit position in the plugin's flat "All Samples" list — higher sorts
    // first; null (the default) means unranked, falling back to release date /
    // pack order. Set via ReorderSounds' "Push to top" (assigns the current
    // global max + 1) or by hand.
    rank: number | null;
};

// A pack's full sound list. One Firestore document per pack, id mirroring the
// sound_packs/{packId} identity doc. ~100 sounds fit well under the 1 MiB cap.
export type DatabaseSoundPackSounds = {
    packId: string;
    sounds: DatabaseSound[];
};

// Field name constants (kept private to this file)
class FieldPaths {
    static readonly SOUNDS = "sounds";
    static readonly UID = "uid";
    static readonly TITLE = "title";
    static readonly IS_DEMO = "isDemo";
    static readonly KEY = "key";
    static readonly BPM = "bpm";
    static readonly LOOP = "loop";
    static readonly DURATION = "duration";
    static readonly THUMBNAIL = "thumbnail";
    static readonly TAG_IDS = "tagIds";
    static readonly INSTRUMENT_TYPE_IDS = "instrumentTypeIds";
    static readonly RANK = "rank";
}

function stringArray(value: unknown): string[] {
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

export class DatabaseSoundPackSoundsModel {

    static readonly COLLECTION_NAME = "sound_packs_sounds";

    private static soundDoc(packId: string) {
        return doc(firestore, this.COLLECTION_NAME, packId);
    }

    private static soundFromFirestore(entry: any): DatabaseSound {
        if (typeof entry !== "object" || !entry || typeof entry[FieldPaths.UID] !== "string") {
            throw new Error("Invalid sound entry structure");
        }
        return {
            uid: entry[FieldPaths.UID],
            title: typeof entry[FieldPaths.TITLE] === 'string' ? entry[FieldPaths.TITLE] : '',
            isDemo: entry[FieldPaths.IS_DEMO] === true,
            key: typeof entry[FieldPaths.KEY] === 'string' ? entry[FieldPaths.KEY] : '',
            bpm: typeof entry[FieldPaths.BPM] === 'number' ? entry[FieldPaths.BPM] : 0,
            loop: typeof entry[FieldPaths.LOOP] === 'boolean' ? entry[FieldPaths.LOOP] : false,
            duration: typeof entry[FieldPaths.DURATION] === 'number' ? entry[FieldPaths.DURATION] : 0,
            thumbnail: typeof entry[FieldPaths.THUMBNAIL] === 'string' ? entry[FieldPaths.THUMBNAIL] : '',
            tagIds: stringArray(entry[FieldPaths.TAG_IDS]),
            instrumentTypeIds: stringArray(entry[FieldPaths.INSTRUMENT_TYPE_IDS]),
            rank: typeof entry[FieldPaths.RANK] === 'number' ? entry[FieldPaths.RANK] : null,
        };
    }

    private static soundToFirestore(sound: DatabaseSound): any {
        return {
            [FieldPaths.UID]: sound.uid,
            [FieldPaths.TITLE]: sound.title,
            [FieldPaths.IS_DEMO]: sound.isDemo,
            [FieldPaths.KEY]: sound.key,
            [FieldPaths.BPM]: sound.bpm,
            [FieldPaths.LOOP]: sound.loop,
            [FieldPaths.DURATION]: sound.duration,
            [FieldPaths.THUMBNAIL]: sound.thumbnail,
            [FieldPaths.TAG_IDS]: sound.tagIds,
            [FieldPaths.INSTRUMENT_TYPE_IDS]: sound.instrumentTypeIds,
            [FieldPaths.RANK]: sound.rank,
        };
    }

    static fromFirestore(data: any, packId: string): DatabaseSoundPackSounds {
        const rawSounds = data && Array.isArray(data[FieldPaths.SOUNDS]) ? data[FieldPaths.SOUNDS] : [];
        const sounds: DatabaseSound[] = [];
        for (const entry of rawSounds) {
            try {
                sounds.push(this.soundFromFirestore(entry));
            } catch (error) {
                console.error(`Error parsing sound in pack ${packId}:`, error);
            }
        }
        return { packId, sounds };
    }

    /**
     * One-shot read of a pack's sound list. Returns an empty sound list when the
     * document does not exist.
     */
    static async getPackSounds(packId: string): Promise<DatabaseSoundPackSounds> {
        const snap = await getDoc(this.soundDoc(packId));
        return this.fromFirestore(snap.exists() ? snap.data() : {}, packId);
    }

    /**
     * Subscribe to a pack's sound list. Returns an Unsubscribe — call it on cleanup.
     */
    static subscribePackSounds(
        packId: string,
        onUpdate: (sounds: DatabaseSoundPackSounds) => void,
        onError?: (error: Error) => void,
    ): Unsubscribe {
        return onSnapshot(
            this.soundDoc(packId),
            (snap) => onUpdate(this.fromFirestore(snap.exists() ? snap.data() : {}, packId)),
            (error) => {
                console.error(`Error in sound list subscription for pack ${packId}:`, error);
                onError?.(error);
            },
        );
    }

    // ====================== ADMIN / PUBLISH METHODS ============================

    /**
     * Parse a pack_manifest.json payload (the array produced by the plugin's
     * SoundPack::toManifestJSON under the "sounds" key) into DatabaseSound[].
     * Manifest entries carry extra authoring fields (descriptor path,
     * loopLengthBars) that the browse model doesn't store — those are dropped.
     * Throws if the payload isn't shaped like a manifest.
     */
    static soundsFromManifest(manifest: any): DatabaseSound[] {
        const rawSounds = manifest && Array.isArray(manifest[FieldPaths.SOUNDS]) ? manifest[FieldPaths.SOUNDS] : null;
        if (!rawSounds) {
            throw new Error('Invalid manifest: missing "sounds" array');
        }
        return rawSounds.map((entry: any, index: number): DatabaseSound => {
            if (typeof entry !== 'object' || !entry || typeof entry[FieldPaths.UID] !== 'string' || !entry[FieldPaths.UID]) {
                throw new Error(`Invalid manifest sound at index ${index}: missing uid`);
            }
            return {
                uid: entry[FieldPaths.UID],
                title: typeof entry[FieldPaths.TITLE] === 'string' ? entry[FieldPaths.TITLE] : '',
                isDemo: entry[FieldPaths.IS_DEMO] === true,
                key: typeof entry[FieldPaths.KEY] === 'string' ? entry[FieldPaths.KEY] : '',
                bpm: typeof entry[FieldPaths.BPM] === 'number' ? entry[FieldPaths.BPM] : 0,
                loop: typeof entry[FieldPaths.LOOP] === 'boolean' ? entry[FieldPaths.LOOP] : false,
                duration: typeof entry[FieldPaths.DURATION] === 'number' ? entry[FieldPaths.DURATION] : 0,
                thumbnail: typeof entry[FieldPaths.THUMBNAIL] === 'string' ? entry[FieldPaths.THUMBNAIL] : '',
                tagIds: stringArray(entry[FieldPaths.TAG_IDS]),
                instrumentTypeIds: stringArray(entry[FieldPaths.INSTRUMENT_TYPE_IDS]),
                rank: null, // new packs start unranked; ranking is an opt-in admin action
            };
        });
    }

    /**
     * Publish a pack's full sound list, overwriting any existing document. The
     * list is generated from the authoring folder (prepareDeliveryPack) — this
     * is the Firestore destination for that serialized payload.
     */
    static async setPackSounds(packId: string, sounds: DatabaseSound[]): Promise<void> {
        await setDoc(this.soundDoc(packId), {
            [FieldPaths.SOUNDS]: sounds.map(s => this.soundToFirestore(s)),
        });
    }

    /**
     * One-shot read of every pack's sound list (admin Firestore rules
     * required). Used by vocabulary maintenance to find sounds referencing
     * tag / instrument-type ids that no longer exist.
     */
    static async getAllPackSounds(): Promise<DatabaseSoundPackSounds[]> {
        const snapshot = await getDocs(collection(firestore, this.COLLECTION_NAME));
        return snapshot.docs.map(d => this.fromFirestore(d.data(), d.id));
    }

    /**
     * Delete a pack's sound list document. Call alongside deleting the pack's
     * identity doc and storage assets.
     */
    static async deletePackSounds(packId: string): Promise<void> {
        await deleteDoc(this.soundDoc(packId));
    }
}
